<template>
  <Transition :name="isMobile ? 'fade-absolute' : 'slide-from-top'">
    <div
      v-if="shouldRender"
      v-show="visible"
      class="page-menu-popup absolute top-0 z-500 w-full overflow-hidden bg-background tablet:right-0 tablet:mt-4 tablet:w-[500px] tablet:rounded-md tablet:bg-scheme-main tablet:shadow-xl"
      :class="{
        'w-full rounded-none border-none belowTablet:left-1/2 belowTablet:w-screen belowTablet:-translate-x-1/2 tablet:!fixed tablet:!bottom-24 tablet:!right-4 tablet:!top-auto tablet:!mt-0 desktop:!w-[700px] desktop:!rounded-[12px]':
          type === 'chat',
      }"
      :style="chatPopupStyle"
    >
      <slot />
      <button
        v-if="type === 'chat'"
        type="button"
        class="absolute right-3 top-3 z-10 flex size-8 items-center justify-center rounded-full bg-black-bis/60 text-white hover:bg-black-bis/90"
        :aria-label="$texts('close', 'Close')"
        data-testid="chat-popup-close"
        @click="emit('close')"
      >
        <SpriteSymbol name="close" class="size-4" />
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import type { Ref } from 'vue'
import { useElementBounding } from '@vueuse/core'

const props = defineProps<{
  visible: boolean
  type: string
}>()

const emit = defineEmits<{
  close: []
}>()

const { $texts } = useEasyTexts()
const { isMobile } = useViewport()

const menuBottomEl = inject<Ref<HTMLElement | undefined>>('menuBottomEl')
const { bottom: menuBottom, update: updateMenuBounding } =
  useElementBounding(menuBottomEl)

const chatPopupStyle = computed(() => {
  if (props.type !== 'chat' || !menuBottom.value) return undefined
  return {
    '--chat-popup-top': `${menuBottom.value}px`,
  }
})

// Whether the popup content should be rendered in the DOM.
// This is so that once the popup has been opened at least once we only hide
// it instead of removing it.
const shouldRender = ref(false)

watch(
  () => props.visible,
  (isOpen) => {
    // Render the popup in the DOM once it becomes open.
    if (isOpen) {
      shouldRender.value = true

      // Force an immediate bounding rect measurement so the chat popup
      // position is correct on the very first open.
      if (props.type === 'chat') {
        updateMenuBounding()
      }
    }
  },
)
</script>

<style lang="postcss">
.page-menu-popup {
  @screen tablet {
    top: calc(var(--page-menu-top) + var(--page-menu-bottom));
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  }
}
</style>
